// @ts-strict-ignore
import * as React from 'react';
import {
    ClockCircleOutlined,
    DashboardOutlined,
    DollarCircleOutlined,
    HeartOutlined,
    TeamOutlined,
    ThunderboltFilled,
    ToolOutlined,
} from '@ant-design/icons';
import {
    AppPlatform,
    DataPointType,
    OrgOverviewBucketSize,
    StatisticCategory,
    StatisticScope,
    StatisticType,
} from '../../types';
import { antBlue, antCyan, antGold, antGrey, antMagenta } from '../styles/globalStyles';
import { ChartStatCategoryPalettes } from '../styles/themeMode';
import Text from 'antd/lib/typography/Text';
import { displayTextForAppPlatform } from '@/helpers/displayHelpers';
import { DatetimeFormat } from '@/timeHelpers';
import dayjs from 'dayjs';

const SAMPLE_TIME_SCOPE = 'in the selected time range.';

export const iconForStatCategory = (category: StatisticCategory) => {
    switch (category) {
        case StatisticCategory.Build:
            return ToolOutlined;
        case StatisticCategory.StabilityAndHealth:
            return HeartOutlined;
        case StatisticCategory.DevOps:
            return DashboardOutlined;
        case StatisticCategory.Work:
            return TeamOutlined;
    }
};

export const displayTitleForStatCategory = (category: StatisticCategory) => {
    switch (category) {
        case StatisticCategory.Build:
            return 'Build';
        case StatisticCategory.StabilityAndHealth:
            return 'Stability + Health';
        case StatisticCategory.DevOps:
            return 'Mobile DevOps';
        case StatisticCategory.Work:
            return 'Work';
    }
};

export const colorForStatCategory = (category: StatisticCategory) => {
    switch (category) {
        case StatisticCategory.Build:
            return antGold.gold6;
        case StatisticCategory.StabilityAndHealth:
            return antMagenta.magenta6;
        case StatisticCategory.DevOps:
            return antCyan.cyan6;
        case StatisticCategory.Work:
            return antBlue.blue6;
    }
};

/**
 * Returns a palette of hardcoded hex colors for canvas-based chart rendering.
 * CSS variables cannot be used with AntV G2/G2Plot, so chart palettes must be
 * provided via `ChartStatCategoryPalettes` from the theme hook.
 */
export const colorPaletteForStatCategory = (category: StatisticCategory, palettes: ChartStatCategoryPalettes) => {
    switch (category) {
        case StatisticCategory.Build:
            return [
                palettes.gold[6],
                palettes.gold[5],
                palettes.gold[4],
                palettes.gold[7],
                palettes.gold[8],
                palettes.gold[3],
            ];
        case StatisticCategory.StabilityAndHealth:
            return [
                palettes.magenta[6],
                palettes.magenta[5],
                palettes.magenta[4],
                palettes.magenta[7],
                palettes.magenta[8],
                palettes.magenta[3],
            ];
        case StatisticCategory.DevOps:
            return [
                palettes.cyan[6],
                palettes.cyan[5],
                palettes.cyan[4],
                palettes.cyan[7],
                palettes.cyan[8],
                palettes.cyan[3],
            ];
        case StatisticCategory.Work:
            return [
                palettes.blue[6],
                palettes.blue[5],
                palettes.blue[4],
                palettes.blue[7],
                palettes.blue[8],
                palettes.blue[3],
            ];
    }
};

// Deprecated. Use metrics from /availableMetrics endpoint.
export const displayOrgOverviewStatisticLabel: (statisticType: StatisticType) => string = (statisticType) => {
    switch (statisticType) {
        case StatisticType.StatTotalTimeSavedFromAutomations:
            return 'Time saved (total)';
        case StatisticType.StatTotalAutomationsCount:
            return 'Runway automations (total)';
        case StatisticType.StatAverageAutomationsCount:
            return 'Runway automations (avg)';
        case StatisticType.StatAverageTimeSavedFromAutomations:
            return 'Time saved (avg)';
        case StatisticType.AppStoreBuildCount:
            return 'App store builds';
        case StatisticType.CIAverageBuildTime:
            return 'Build time';
        case StatisticType.CIWorkflowSuccessPercent:
            return 'Build workflow success rate';
        case StatisticType.CommitCount:
            return 'Number of commits';
        case StatisticType.ItemsCompleted:
            return 'Items of work completed';
        case StatisticType.FixesCount:
            return 'Number of fixes';
        case StatisticType.FixRequestsCount:
            return 'Number of fix requests';
        case StatisticType.FixRequestsApproved:
            return 'Fix requests approved';
        case StatisticType.FixRequestsRejected:
            return 'Fix requests rejected';
        case StatisticType.PercentWorkItemsInFixRequests:
            return 'Completed work items fix percentage';
        case StatisticType.FilesChangedCount:
            return 'Number of files changed';
        case StatisticType.InReviewDuration:
            return 'Time in store review';
        case StatisticType.LinesOfCodeChanged:
            return 'Lines of code';
        case StatisticType.ReleaseDuration:
            return 'Release duration';
        case StatisticType.WaitingForReviewDuration:
            return 'Time waiting for store review';
        case StatisticType.CrashFreeUsers:
            return 'Crash-free users';
        case StatisticType.CrashFreeSessions:
            return 'Crash-free sessions';
        case StatisticType.AppStoreBuildBundleDownloadSize:
            return 'Bundle download size';
        case StatisticType.AppStoreBuildBundleInstallSize:
            return 'Bundle install size';
        case StatisticType.BetaCrashFreeUsers:
            return 'Beta crash-free users';
        case StatisticType.BetaCrashFreeSessions:
            return 'Beta crash-free sessions';
        case StatisticType.StatAverageCustomerRating:
            return 'User rating (release specific)';
        case StatisticType.TotalHotfixesCount:
            return 'Total hotfixes';
        case StatisticType.HotfixReleaseDuration:
            return 'Hotfix release duration';
        case StatisticType.StatReleaseDurations:
            return 'Lifecycle timing';
        case StatisticType.StatAverageTimeToRecover:
            return 'Time to recovery';
        case StatisticType.StatFailureRate:
            return 'Release failure rate';
        case StatisticType.StatAverageCLIApprovalTime:
            return 'CLI approval time';
        case StatisticType.StatAverageReleaseStepCompletionTime:
            return 'Release step completion time';
        case StatisticType.StatReleaseFrequency:
            return 'Release frequency';
        case StatisticType.StatAverageAppCustomerRating:
            return 'User rating (all releases)';
        default:
            return null;
    }
};

export const tooltipForScope = (scope: StatisticScope, statisticType?: StatisticType): string | null => {
    const allNonHotfixReleases = 'all releases (excluding hotfixes)';
    let scopeMessage = '';

    switch (scope) {
        case StatisticScope.App:
            scopeMessage = `completed`;
            break;
        case StatisticScope.AppGroup:
            scopeMessage = `for all apps in the group completed`;
            break;
        case StatisticScope.Org:
            scopeMessage = `for all apps in the org completed`;
            break;
    }

    switch (statisticType) {
        case StatisticType.StatReleaseFrequency:
            return `The average time between releases, rounded to the nearest typical cadence frequency (weekly, biweekly, monthly, quarterly, yearly).`;
        case StatisticType.StatFailureRate:
            return `The percentage of releases that resulted in failure.`;
        case StatisticType.StatAverageTimeToRecover:
            return `The average time between when a failure for a release was detected and when a hotfix was subsequently issued.`;
        case StatisticType.StatAverageReleaseStepCompletionTime:
            return `The average amount of time it takes to complete any release step.`;
        case StatisticType.StatAverageCLIApprovalTime:
            return `The average amount of time it takes to complete a checklist item.`;
        case StatisticType.StatReleaseDurations:
            return `The average amount of time a work item spends in each stage of the development lifecycle.`;
        case StatisticType.StatAverageAppCustomerRating:
            return `Average considering all releases ${scopeMessage} ${SAMPLE_TIME_SCOPE} iOS & Android apps only.`;
        case StatisticType.HotfixReleaseDuration:
            return `Average considering ${allNonHotfixReleases} ${scopeMessage} ${SAMPLE_TIME_SCOPE} iOS apps only.`;
        case StatisticType.WaitingForReviewDuration:
            return `Average considering ${allNonHotfixReleases} ${scopeMessage} ${SAMPLE_TIME_SCOPE} Apple only.`;
        case StatisticType.InReviewDuration:
            return `Average considering ${allNonHotfixReleases} ${scopeMessage} ${SAMPLE_TIME_SCOPE}`;
        case StatisticType.StatAverageCustomerRating:
            return `Average for written reviews considering ${allNonHotfixReleases} ${scopeMessage} ${SAMPLE_TIME_SCOPE} iOS & Android apps only.`;
        case StatisticType.TotalHotfixesCount:
            return `Total number of hotfixes issued ${SAMPLE_TIME_SCOPE}`;
        case StatisticType.AppStoreBuildBundleInstallSize:
            return `Average considering ${allNonHotfixReleases} ${scopeMessage} ${SAMPLE_TIME_SCOPE} install size, iOS apps only.`;
        case StatisticType.AppStoreBuildBundleDownloadSize:
            return `Average considering ${allNonHotfixReleases} ${scopeMessage} ${SAMPLE_TIME_SCOPE} For Android AABs, download size is averaged across device configurations (not universal).`;
        default:
            return `Average considering ${allNonHotfixReleases} ${scopeMessage} ${SAMPLE_TIME_SCOPE}`;
    }
};

export const valueIconForStatisticType = (statisticType: StatisticType) => {
    switch (statisticType) {
        case StatisticType.StatAverageAutomationsCount:
        case StatisticType.StatTotalAutomationsCount:
            return ThunderboltFilled;
        case StatisticType.StatTotalTimeSavedFromAutomations:
        case StatisticType.StatAverageTimeSavedFromAutomations:
            return ClockCircleOutlined;
        case StatisticType.StatTotalCostSavingsFromAutomations:
        case StatisticType.StatAverageCostSavingsFromAutomations:
            return DollarCircleOutlined;
        default:
            return null;
    }
};

export const statViewOverride: (props: {
    type: StatisticType;
    value: number;
    dataPoints: DataPointType[];
}) => React.ReactNode | null = ({ type, value }) => {
    let content: React.ReactNode | null = null;
    switch (type) {
        case StatisticType.TotalHotfixesCount:
            if (value === 0) {
                content = (
                    <Text
                        style={{
                            color: antGrey.grey6,
                            width: 162,
                            marginLeft: 20,
                        }}
                    >
                        No hotfixes {SAMPLE_TIME_SCOPE}
                        <br />
                        <br />
                        <strong>Nice!</strong>
                    </Text>
                );
            }
            break;
        case StatisticType.StatFailureRate:
            if (value === 0) {
                content = (
                    <Text
                        style={{
                            color: antGrey.grey6,
                            width: 162,
                            marginLeft: 20,
                        }}
                    >
                        No release failures over this time period.
                        <br />
                        <br />
                        <strong>Nice!</strong>
                    </Text>
                );
            }
            break;
        default:
            return null;
    }

    if (!content) {
        return null;
    }

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                height: '100%',
            }}
        >
            {content}
        </div>
    );
};

export const GetAppNameWithPlatform = (appName: string, platform: AppPlatform) => {
    return `${appName} (${displayTextForAppPlatform(platform)})`;
};

export const GetResolvedBarSize = (categories: any[]) => {
    if (categories.length <= 2) {
        return 120;
    }

    if (categories.length === 3) {
        return 88;
    }

    return 56;
};

export const DEFAULT_ORG_OVERVIEW_BUCKET_SIZE = OrgOverviewBucketSize.Weekly;

export const ORG_OVERVIEW_BUCKET_SIZE_OPTIONS = [
    {
        label: 'Daily',
        value: OrgOverviewBucketSize.Daily,
    },
    {
        label: 'Weekly',
        value: OrgOverviewBucketSize.Weekly,
    },
    {
        label: 'Monthly',
        value: OrgOverviewBucketSize.Monthly,
    },
    {
        label: 'Quarterly',
        value: OrgOverviewBucketSize.Quarterly,
    },
];

export function formatBucketDateLabel(value: string, bucketSize: OrgOverviewBucketSize, includeYear = false) {
    const date = dayjs.utc(value);

    switch (bucketSize) {
        case OrgOverviewBucketSize.Monthly:
            return date.format(includeYear ? 'MMMM YYYY' : 'MMMM');
        case OrgOverviewBucketSize.Quarterly:
            return date.format(includeYear ? '[Q]Q YYYY' : '[Q]Q');
        default:
            return date.format(includeYear ? DatetimeFormat.ShortMonthDateYear : DatetimeFormat.ShortMonthDate);
    }
}

export function getOrgOverviewTimeBucketLabel(
    dataPoint: {
        timestamp?: string;
        sampleStartedAt?: string;
        sampleEndedAt?: string;
    },
    bucketSize: OrgOverviewBucketSize,
) {
    const sampleStartedAt = dataPoint.sampleStartedAt ?? dataPoint.timestamp;
    if (!sampleStartedAt) {
        return '';
    }

    if (bucketSize === OrgOverviewBucketSize.Weekly && dataPoint.sampleEndedAt) {
        return `${formatBucketDateLabel(sampleStartedAt, OrgOverviewBucketSize.Daily, true)} - ${formatBucketDateLabel(
            dataPoint.sampleEndedAt,
            OrgOverviewBucketSize.Daily,
            true,
        )}`;
    }

    return formatBucketDateLabel(sampleStartedAt, bucketSize, true);
}
